/**
 * Risoluzione di chat @lid duplicate via cache, name match e WAHA API in background.
 * @lid sono "linked device" IDs di WAHA NOWEB (formato diverso da phone @c.us).
 */

import type { ChatOverview } from '../../types/whatsappChat';
import { getWahaService } from '../../services/wahaApiService';
import { debugLog } from '../../../components/whatsapp2/chat/ChatDebugPanel';
import { _lidResolutionState } from './caches';
import { matchCusByName, removeLidDuplicates } from '../../services/whatsapp/lidDuplicates';

/**
 * Filtra chat @lid duplicate. Tre livelli di matching (in ordine):
 * 1. lidToCusMap — mapping esatto @lid → @c.us (da API o localStorage)
 * 2. Nome uguale o prefisso — "Sara Risoli" (@lid) matcha "Sara Risoli Obly" (@c.us), solo se il candidato e' unico
 * 3. API resolution in background — per i @lid non risolti, chiama WAHA e salva per la prossima volta
 */
export function filterLidDuplicates(
  chats: ChatOverview[],
  lidToCusMap?: Map<string, string>,
  onResolvedLids?: (resolved: Map<string, string>) => void,
): ChatOverview[] {
  // Set di tutti gli id @c.us per match veloce
  const cusIds = new Set<string>();
  const cusChats: ChatOverview[] = [];
  for (const c of chats) {
    if (c.id.endsWith('@c.us')) {
      cusIds.add(c.id);
      cusChats.push(c);
    }
  }

  const unresolvedLids: string[] = [];
  const before = chats.length;

  // Il doppione @lid sparisce ma il suo ultimo messaggio passa alla chat @c.us
  // (lidDuplicates.ts): prima la chat restava con la data vecchia, fuori posto.
  const { chats: filtered } = removeLidDuplicates(chats, (c) => {
    // Livello 1: mapping esatto da cache
    const mappedCus = lidToCusMap?.get(c.id);
    if (mappedCus && cusIds.has(mappedCus)) return mappedCus;

    // Livello 2: stesso nome, una sola chat @c.us corrispondente
    const byName = matchCusByName(c.name, cusChats);
    if (byName) return byName;

    // Non risolto: tieni la chat ma segna per risoluzione API
    unresolvedLids.push(c.id);
    return null;
  });

  const removed = before - filtered.length;
  if (removed > 0) {
    debugLog('ChatList', `filterLidDuplicates: rimossi ${removed} duplicati @lid (${unresolvedLids.length} non risolti)`);
  }

  // Livello 3: risolvi i @lid rimasti via API in background
  if (unresolvedLids.length > 0 && onResolvedLids) {
    resolveUnknownLids(unresolvedLids, cusIds, onResolvedLids);
  }

  return filtered;
}

/**
 * Risolve @lid sconosciuti via WAHA API in background.
 * Se trova un mapping @lid → @c.us che corrisponde a una chat esistente,
 * chiama il callback per salvarlo e ri-triggerare il filtro.
 */
async function resolveUnknownLids(
  lids: string[],
  existingCusIds: Set<string>,
  onResolved: (resolved: Map<string, string>) => void,
) {
  if (_lidResolutionState.inProgress) return;

  // Filtra LID già tentati in questa sessione
  const toResolve = lids.filter(lid => !_lidResolutionState.attempted.has(lid));
  if (toResolve.length === 0) return;

  _lidResolutionState.inProgress = true;

  try {
    const service = getWahaService();
    const resolved = new Map<string, string>();

    // Livello 1: usa il mapping dal contacts list (getAllContacts cattura number per @lid)
    let lidPhoneMap = service.getLidPhoneMap();
    if (lidPhoneMap.size === 0) {
      await service.getContactsMap().catch(() => {});
      lidPhoneMap = service.getLidPhoneMap();
    }
    for (const lid of toResolve) {
      const cusId = lidPhoneMap.get(lid);
      if (cusId && existingCusIds.has(cusId)) {
        resolved.set(lid, cusId);
        _lidResolutionState.attempted.add(lid);
      }
    }

    // Livello 2: per i rimanenti, prova resolveLid API + getContactInfo come fallback
    const remaining = toResolve.filter(lid => !resolved.has(lid));
    const batchSize = 5;
    for (let i = 0; i < remaining.length; i += batchSize) {
      const batch = remaining.slice(i, i + batchSize);
      const results = await Promise.allSettled(
        batch.map(async lid => {
          // Prima prova resolveLid
          const phone = await service.resolveLid(lid);
          if (phone) return { lid, phone };
          // Fallback: getContactInfo potrebbe avere il number
          const info = await service.getContactInfo(lid);
          if (info?.number) return { lid, phone: info.number };
          return { lid, phone: null };
        })
      );

      for (let j = 0; j < results.length; j++) {
        const result = results[j];
        _lidResolutionState.attempted.add(batch[j]);
        if (result.status === 'fulfilled' && result.value.phone) {
          const cusId = result.value.phone.includes('@')
            ? result.value.phone
            : `${result.value.phone}@c.us`;
          resolved.set(result.value.lid, cusId);
        }
      }
    }

    if (resolved.size > 0) {
      debugLog('ChatList', `resolveLids: risolti ${resolved.size}/${toResolve.length} @lid via API`, {
        resolved: [...resolved.entries()].map(([lid, cus]) => `${lid.slice(0, 15)}→${cus.slice(0, 15)}`),
      });
      onResolved(resolved);
    } else {
      debugLog('ChatList', `resolveLids: 0/${toResolve.length} @lid risolti (nessun match @c.us)`);
    }
  } catch (e) {
    toResolve.forEach(lid => _lidResolutionState.attempted.add(lid));
    console.warn('[LidResolve] Error:', e);
  } finally {
    _lidResolutionState.inProgress = false;
  }
}

/**
 * Callback invocato quando i @lid vengono risolti via API.
 * Salva i mapping in lidToCusMap + localStorage e rimuove i duplicati dalle chat.
 *
 * Refactored: invece di accedere a Zustand get/set, riceve esplicitamente:
 * - chats correnti
 * - lidToCusMap corrente (per leggere i mapping appena salvati)
 * - saveLidMapping function (per persistere ogni mapping in localStorage)
 *
 * Ritorna le chats filtrate (o `null` se nessun cambiamento). Il caller applica
 * il `set({ chats })` se il return è non-null.
 */
export function handleLidResolution(
  resolved: Map<string, string>,
  chats: ChatOverview[],
  lidToCusMap: Map<string, string>,
  saveLidMapping: (lid: string, cus: string) => void,
): ChatOverview[] | null {
  // Salva ogni mapping (persiste in localStorage tramite l'azione store)
  for (const [lid, cus] of resolved) {
    saveLidMapping(lid, cus);
  }

  // Rimuovi le chat @lid che ora hanno un corrispondente @c.us
  const cusIds = new Set(chats.filter(c => c.id.endsWith('@c.us')).map(c => c.id));
  // Costruisci la mappa effettiva post-update (resolved + esistenti)
  const effectiveMap = new Map(lidToCusMap);
  for (const [lid, cus] of resolved) effectiveMap.set(lid, cus);

  const { chats: filteredChats } = removeLidDuplicates(chats, (c) => {
    const mappedCus = effectiveMap.get(c.id);
    return mappedCus && cusIds.has(mappedCus) ? mappedCus : null;
  });

  if (filteredChats.length < chats.length) {
    debugLog('ChatList', `handleLidResolution: rimossi ${chats.length - filteredChats.length} @lid dopo risoluzione API`);
    return filteredChats;
  }
  return null;
}
